// src/pages/RegisterPage.js

import React, { useState, forwardRef } from 'react';
import { useNavigate, Link as RouterLink } from 'react-router-dom';
import { Box, Button, TextField, Typography, Paper, Alert, Link, InputAdornment, IconButton, CircularProgress } from '@mui/material';
import Visibility from '@mui/icons-material/Visibility';
import VisibilityOff from '@mui/icons-material/VisibilityOff';
import { IMaskInput } from 'react-imask'; 
import { cpf as cpfValidator } from 'cpf-cnpj-validator';
import api from '../services/api';

const CpfMask = forwardRef(function CpfMask(props, ref) {
  const { onChange, ...other } = props;
  return (
    <IMaskInput
      {...other}
      mask="000.000.000-00"
      inputRef={ref}
      onAccept={(value) => onChange({ target: { name: props.name, value } })}
      overwrite
    />
  );
});

const TelefoneMask = forwardRef(function TelefoneMask(props, ref) {
  const { onChange, ...other } = props;
  return (
    <IMaskInput
      {...other}
      mask="(00) 00000-0000"
      inputRef={ref}
      onAccept={(value) => onChange({ target: { name: props.name, value } })}
      overwrite
    />
  );
});


const RegisterPage = () => {
  const [nomeCompleto, setNomeCompleto] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [cpf, setCpf] = useState('');
  const [telefone, setTelefone] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const navigate = useNavigate();

  const handleRegister = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');

    if (password !== confirmPassword) {
      setError('As senhas não coincidem.');
      return;
    }
    // Removemos a formatação da máscara antes de validar
    const unmaskedCpf = cpf.replace(/[^\d]/g, '');
    if (unmaskedCpf && !cpfValidator.isValid(unmaskedCpf)) {
      setError('O CPF digitado não é válido.');
      return;
    }

    setIsLoading(true);
    try {
      await api.post('/registrar/', {
        nome_completo: nomeCompleto,
        email: email,
        password: password,
        cpf: unmaskedCpf, 
        telefone: telefone.replace(/[^\d]/g, ''), 
      });
      setSuccess('Usuário criado com sucesso! Você será redirecionado para o login.');
      setTimeout(() => navigate('/login'), 3000);
    } catch (err) {
      const errorMsg = err.response?.data?.error || 'Ocorreu um erro desconhecido.';
      setError(errorMsg);
    } finally {
      setIsLoading(false);
    }
  };

  const inputStyle = {
    '& .MuiOutlinedInput-root': {
      borderRadius: 2,
      backgroundColor: 'rgba(255, 255, 255, 0.06)',
      color: '#FFFFFF',
      '& fieldset': { borderColor: 'rgba(255, 255, 255, 0.20)' },
      '&:hover fieldset': { borderColor: 'rgba(255, 255, 255, 0.40)' },
      '&.Mui-focused fieldset': { borderColor: '#3897F0' },
    },
    '& .MuiInputLabel-root': { color: 'rgba(255, 255, 255, 0.65)' },
    '& .MuiInputLabel-root.Mui-focused': { color: '#3897F0' },
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center', minHeight: '100vh', background: 'linear-gradient(135deg, #030D1B 0%, #071933 50%, #020914 100%)', p: 2 }}>
      <Paper 
        component="form" 
        onSubmit={handleRegister} 
        elevation={12} 
        sx={{ 
          padding: { xs: 3, sm: 4.5 }, 
          width: '100%', 
          maxWidth: 460, 
          textAlign: 'center', 
          borderRadius: 4, 
          backgroundColor: 'rgba(7, 25, 51, 0.88)',
          backdropFilter: 'blur(20px)',
          border: '1px solid rgba(255, 255, 255, 0.12)',
          boxShadow: '0 20px 50px rgba(0, 0, 0, 0.6)',
          color: '#FFFFFF',
        }}
      >
        <Box sx={{ mb: 1.5, display: 'flex', justifyContent: 'center' }}>
          <img src="/logo-completa.png" alt="ITAPOÁ SIGN" style={{ maxHeight: 105, maxWidth: '100%', objectFit: 'contain', borderRadius: 8 }} />
        </Box>
        <Typography variant="h5" sx={{ color: '#FFFFFF', fontWeight: 700, letterSpacing: '0.5px' }}>
          Criar Nova Conta
        </Typography>
        <Typography variant="body2" sx={{ mb: 2.5, color: 'rgba(255, 255, 255, 0.65)' }}>
          ITAPOÁ SIGN - Assinatura Digital
        </Typography>
        
        {error && <Alert severity="error" sx={{ my: 2, textAlign: 'left' }}>{error}</Alert>}
        {success && <Alert severity="success" sx={{ my: 2, textAlign: 'left' }}>{success}</Alert>}

        <TextField fullWidth label="Nome Completo" value={nomeCompleto} onChange={(e) => setNomeCompleto(e.target.value)} margin="normal" required sx={inputStyle} />
        <TextField fullWidth label="Email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} margin="normal" required sx={inputStyle} />
        
        <TextField
          fullWidth
          label="CPF"
          value={cpf}
          onChange={(e) => setCpf(e.target.value)}
          margin="normal"
          InputProps={{ inputComponent: CpfMask }}
          sx={inputStyle}
        />
        <TextField
          fullWidth
          label="Telefone"
          value={telefone}
          onChange={(e) => setTelefone(e.target.value)}
          margin="normal"
          InputProps={{ inputComponent: TelefoneMask }}
          sx={inputStyle}
        />
        
        <TextField 
          fullWidth 
          label="Senha" 
          type={showPassword ? 'text' : 'password'}
          value={password} 
          onChange={(e) => setPassword(e.target.value)} 
          margin="normal" 
          required
          InputProps={{
            endAdornment: (
              <InputAdornment position="end">
                <IconButton onClick={() => setShowPassword(!showPassword)} edge="end" sx={{ color: 'rgba(255, 255, 255, 0.65)' }}>
                  {showPassword ? <VisibilityOff /> : <Visibility />}
                </IconButton>
              </InputAdornment>
            ),
          }}
          sx={inputStyle}
        />
        
        <TextField 
          fullWidth 
          label="Confirmar Senha" 
          type={showPassword ? 'text' : 'password'}
          value={confirmPassword}
          onChange={(e) => setConfirmPassword(e.target.value)}
          margin="normal"
          required
          sx={inputStyle}
        />
        
        <Button 
          type="submit" 
          fullWidth 
          variant="contained" 
          sx={{ 
            mt: 3, 
            py: 1.4, 
            borderRadius: 2.5,
            fontSize: '1rem',
            fontWeight: 600,
            textTransform: 'none',
            background: 'linear-gradient(to right, #0D6EFD, #0052CC)',
            boxShadow: '0 4px 16px rgba(13, 110, 253, 0.45)',
            '&:hover': {
              background: 'linear-gradient(to right, #0b5ed7, #0040a8)',
            }
          }} 
          disabled={isLoading}
        >
          {isLoading ? <CircularProgress size={24} color="inherit" /> : 'Cadastrar'}
        </Button>
        
        <Link component={RouterLink} to="/login" sx={{ display: 'block', mt: 2.5, color: '#3897F0', fontWeight: 500, textDecoration: 'none', '&:hover': { textDecoration: 'underline' } }}>
          Já tem uma conta? Faça o login
        </Link>
      </Paper>
      
      <Box sx={{ mt: 3, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1 }}>
        <Link 
          component={RouterLink} 
          to="/politica-de-privacidade" 
          variant="caption" 
          sx={{ color: '#3897F0', textDecoration: 'none', '&:hover': { textDecoration: 'underline' } }}
        >
          Política de Privacidade e Proteção de Dados
        </Link>
        <Typography variant="caption" sx={{ color: 'rgba(255, 255, 255, 0.45)', textAlign: 'center', letterSpacing: '0.3px' }}>
          Powered by Câmara Municipal de Itapoá-SC
        </Typography>
      </Box>
    </Box>
  );
};

export default RegisterPage;